import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';
import { peekPendingPassword, clearPendingPassword } from '../utils/pendingPasswordChange';
import PasswordRequirements, { getPasswordStrength } from '../components/PasswordRequirements';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(() => ({
    currentPassword: peekPendingPassword() || '',
    newPassword: '',
    confirmPassword: ''
  }));
  // After a forced change on login we already have the current password,
  // so don't ask the user to retype it.
  const [currentPrefilled, setCurrentPrefilled] = useState(() => !!peekPendingPassword());

  useEffect(() => {
    clearPendingPassword();
  }, []);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const passwordStrength = getPasswordStrength(formData.newPassword);

  // Check if this is a required password change
  const isRequired = location.state?.required || false;
  const message = location.state?.message || 'Altere sua senha';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors([]);
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);

    // Client-side validation
    const validationErrors = [];

    if (!formData.currentPassword) {
      validationErrors.push('Senha atual é obrigatória');
    }

    if (!formData.newPassword) {
      validationErrors.push('Nova senha é obrigatória');
    }

    if (formData.newPassword !== formData.confirmPassword) {
      validationErrors.push('As novas senhas não coincidem');
    }

    if (formData.currentPassword === formData.newPassword) {
      validationErrors.push('A nova senha deve ser diferente da senha atual');
    }

    // Check strength requirements
    if (!passwordStrength.length) {
      validationErrors.push('A senha deve ter pelo menos 8 caracteres');
    }
    if (!passwordStrength.uppercase) {
      validationErrors.push('A senha deve conter pelo menos uma letra maiúscula');
    }
    if (!passwordStrength.lowercase) {
      validationErrors.push('A senha deve conter pelo menos uma letra minúscula');
    }
    if (!passwordStrength.number) {
      validationErrors.push('A senha deve conter pelo menos um número');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      setSuccess(true);

      // Redirect after 2 seconds
      setTimeout(() => {
        if (isRequired) {
          // If this was a required change, go to dashboard
          navigate('/dashboard');
        } else {
          // Otherwise go back
          navigate(-1);
        }
      }, 2000);

    } catch (error) {
      console.error('Change password error:', error);

      if (error.response?.data?.code === 'INVALID_CURRENT_PASSWORD' && currentPrefilled) {
        setCurrentPrefilled(false);
        setFormData(prev => ({ ...prev, currentPassword: '' }));
      }

      if (error.response?.data?.errors) {
        // Server validation errors
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors([error.response.data.message]);
      } else {
        setErrors(['Erro ao alterar senha. Por favor, tente novamente.']);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-100 via-brand-200 to-brand-400 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white rounded-2xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-brand-100 rounded-full flex items-center justify-center">
            <Lock className="h-8 w-8 text-brand-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Alterar Senha
          </h2>
          {isRequired && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-700">{message}</p>
              </div>
            </div>
          )}
          {!isRequired && (
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm text-green-700">
                Senha alterada com sucesso! Redirecionando...
              </p>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <div className="flex items-start">
              <XCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  {errors.length === 1 ? 'Erro' : 'Ocorreram múltiplos erros'}
                </h3>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Current Password */}
          {!currentPrefilled && (
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Senha Atual
            </label>
            <div className="relative">
              <input
                id="currentPassword"
                name="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                required
                value={formData.currentPassword}
                onChange={handleChange}
                disabled={loading || success}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Digite a senha atual"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('current')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={loading || success}
              >
                {showPasswords.current ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
          )}

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Nova Senha
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                required
                value={formData.newPassword}
                onChange={handleChange}
                disabled={loading || success}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Digite a nova senha"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('new')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={loading || success}
              >
                {showPasswords.new ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>

            {formData.newPassword && <PasswordRequirements password={formData.newPassword} />}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading || success}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Confirme a nova senha"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('confirm')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={loading || success}
              >
                {showPasswords.confirm ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <XCircle className="h-4 w-4 mr-1" />
                As senhas não coincidem
              </p>
            )}
            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
              <p className="mt-2 text-sm text-green-600 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                As senhas coincidem
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            {!isRequired && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading || success}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading || success || passwordStrength.score < 100 || formData.newPassword !== formData.confirmPassword}
              className={`${isRequired ? 'w-full' : 'flex-1'} py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Alterando Senha...
                </>
              ) : (
                'Alterar Senha'
              )}
            </button>
          </div>
        </form>

        {/* Security Note */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-600">
            <strong>Nota de Segurança:</strong> Sua senha expirará a cada 90 dias.
            Você não pode reutilizar suas últimas 5 senhas. Por segurança, sua sessão
            expirará após 30 minutos de inatividade.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
